'use client';

import * as ToastPrimitives from '@radix-ui/react-toast';
import { type VariantProps, cva } from 'class-variance-authority';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
} from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 z-[100000] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

// Lexomat-aligned toast design with three-color gradients, subtle borders, and backdrop blur
const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-3 overflow-hidden rounded-xl border p-4 pr-10 shadow-lg backdrop-blur-sm transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        default: 'bg-surface-card border-line-default text-content-primary',
        destructive:
          'destructive group bg-status-error-bg border-status-error/30 text-status-error-text',
        success:
          'bg-status-success-bg border-status-success/30 text-status-success-text',
        warning:
          'bg-status-warning-bg border-status-warning/30 text-status-warning-text',
        info: 'bg-status-info-bg border-status-info/30 text-status-info-text',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

// Toast variant type
type ToastVariant = 'default' | 'destructive' | 'success' | 'warning' | 'info';

// Icon mapping for each variant
const variantIcons: Record<
  ToastVariant,
  | typeof CheckCircle2
  | typeof AlertTriangle
  | typeof AlertCircle
  | typeof Info
  | null
> = {
  default: null,
  destructive: AlertTriangle,
  success: CheckCircle2,
  warning: AlertCircle,
  info: Info,
};

// Icon background classes for each variant
const iconBackgroundClasses: Record<ToastVariant, string> = {
  default: 'bg-surface-muted',
  destructive: 'bg-status-error/15',
  success: 'bg-status-success/15',
  warning: 'bg-status-warning/15',
  info: 'bg-status-info/15',
};

// Icon color classes for each variant
const iconColorClasses: Record<ToastVariant, string> = {
  default: 'text-content-secondary',
  destructive: 'text-status-error',
  success: 'text-status-success',
  warning: 'text-status-warning',
  info: 'text-status-info',
};

// Toast icon component for use in Toaster
interface ToastIconProps {
  variant?: ToastVariant | null;
}

function ToastIcon({ variant }: ToastIconProps) {
  const variantKey = variant ?? 'default';
  const IconComponent = variantIcons[variantKey];

  if (!IconComponent) return null;

  return (
    <div
      className={cn(
        'flex-shrink-0 p-2 rounded-lg',
        iconBackgroundClasses[variantKey]
      )}
    >
      <IconComponent
        className={cn('h-5 w-5', iconColorClasses[variantKey])}
        strokeWidth={2}
      />
    </div>
  );
}

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close> & {
    /**
     * Screen-reader name for the close control. The button's only visible
     * content is a decorative `<X>` icon, so without this it has NO accessible
     * name at all — axe `button-name`, impact **critical** (WCAG 2.1 AA
     * 4.1.2). Measured on the built site 2026-09-22: violated on `/sk` and
     * `/en`. The gate had never caught it because the toast mounts after axe
     * runs; see `frontend/e2e/accessibility.e2e.ts`.
     *
     * Defaults to English so the primitive is never nameless even when
     * rendered outside `NextIntlClientProvider` — matching `dialog.tsx` and
     * `sheet.tsx`, which hardcode the same fallback. `Toaster` overrides it
     * with the localized string.
     */
    readonly closeLabel?: string;
  }
>(({ className, closeLabel = 'Close', ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'absolute right-1 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-status-error group-[.destructive]:hover:text-status-error-hover',
      className
    )}
    toast-close=''
    {...props}
  >
    <X className='h-4 w-4' aria-hidden='true' />
    <span className='sr-only'>{closeLabel}</span>
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('text-sm font-semibold [&+div]:text-xs', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('text-sm opacity-90', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  type ToastVariant,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastAction,
  ToastClose,
  ToastIcon,
};
